// src/lib/localizationGuard.js
// Defensive guard for client localizations to avoid crashes when translations are missing.

/* eslint-disable no-undef */
/* global window */

// Avoid referencing `import` directly; rely on Vite's import.meta if available.
const isDev = !!(typeof import.meta !== "undefined" && import.meta.env && import.meta.env.DEV);
let warnedOnce = false;
let handlersInstalled = false;

function isLocalizationError(value) {
  const name = (value && value.name) || "";
  const message = (value && value.message) || value || "";
  const combined = `${name} ${message}`;
  return /RegisterClientLocalizationsError/i.test(name) || /RegisterClientLocalizationsError/i.test(message) || (/translations/i.test(combined) && /localization|register/i.test(combined));
}

function warnOnce(message, context) {
  if (isDev && !warnedOnce) {
    try {
      console.warn("[localizationGuard]", message, context || {});
    } catch (_) {
      // no-op
    }
    warnedOnce = true;
  }
}

function ensureGlobalContainer() {
  try {
    if (!window) return;
    const g = window;
    if (!g.__mudoLocalizations || typeof g.__mudoLocalizations !== "object") {
      g.__mudoLocalizations = { translations: {} };
      warnOnce("Initialized missing __mudoLocalizations container", {});
    } else if (!g.__mudoLocalizations.translations || typeof g.__mudoLocalizations.translations !== "object") {
      g.__mudoLocalizations.translations = {};
      warnOnce("Filled missing __mudoLocalizations.translations with {}", {});
    }
  } catch (_) {
    // Swallow any unexpected errors to remain defensive
  }
}

function installGlobalHandlers() {
  if (handlersInstalled) return;
  if (typeof window === "undefined") return;
  handlersInstalled = true;

  const swallow = (event) => {
    const reason = event?.reason ?? event?.error;
    if (isLocalizationError(reason)) {
      warnOnce("Suppressed RegisterClientLocalizationsError", {
        name: reason?.name,
        message: reason?.message || reason,
      });
      if (typeof event?.preventDefault === "function") {
        event.preventDefault();
      }
    }
  };

  window.addEventListener("unhandledrejection", swallow);
  window.addEventListener("error", swallow);
}

function safeRegister(data) {
  try {
    if (!window) return;
    const g = window;
    ensureGlobalContainer();
    const incoming = (data && typeof data === "object") ? data : {};
    if (data && typeof data !== "object") {
      warnOnce("registerClientLocalizations called with non-object payload", { type: typeof data });
    }
    const translations = incoming.translations && typeof incoming.translations === "object"
      ? incoming.translations
      : {};
    if (!incoming.translations) {
      warnOnce("Missing translations in register payload; defaulting to {}", { keys: Object.keys(incoming || {}) });
    }
    const current = g.__mudoLocalizations || { translations: {} };
    g.__mudoLocalizations = {
      ...current,
      ...incoming,
      translations,
    };
    return g.__mudoLocalizations;
  } catch (e) {
    warnOnce("registerClientLocalizations threw; swallowed to prevent crash", { error: String(e && e.message || e) });
    // Ensure container still exists
    ensureGlobalContainer();
    return window && window.__mudoLocalizations;
  }
}

// Run on import
ensureGlobalContainer();
installGlobalHandlers();

// Provide a defensive global register function if absent, or wrap existing one
try {
  if (typeof window !== "undefined") {
    if (typeof window.registerClientLocalizations !== "function") {
      window.registerClientLocalizations = safeRegister;
    } else {
      const orig = window.registerClientLocalizations;
      window.registerClientLocalizations = function wrappedRegister(data) {
        try {
          // Normalize missing translations to {}
          const normalized = (data && typeof data === "object") ? { ...data } : {};
          if (!normalized.translations) {
            normalized.translations = {};
            warnOnce("Patched missing translations for existing registerClientLocalizations", {});
          }
          const res = orig.call(this, normalized);
          // Ensure global container remains sane
          ensureGlobalContainer();
          return res;
        } catch (e) {
          return safeRegister(data);
        }
      };
    }
  }
} catch (_) {
  // Don't allow guard to cause failures
}

export function ensureTranslationsContainer() {
  ensureGlobalContainer();
  return (typeof window !== "undefined") ? (window.__mudoLocalizations || { translations: {} }) : { translations: {} };
}

export function registerClientLocalizationsSafe(data) {
  return safeRegister(data);
}
